/*
 * Nishene — comportements communs, sans bibliothèque.
 * - Tiroirs et fenêtres : <dialog> natif (focus piégé et Échap fournis par le
 *   navigateur). [data-dialog-open][aria-controls] ouvre, [data-dialog-close]
 *   ferme, un clic sur le fond ferme.
 * - Vidéos [data-autoplay-video] : lecture muette seulement quand elles sont à
 *   l'écran ; en mouvement réduit, arrêtée sur son image jusqu'à un geste.
 *   Le bouton [data-video-toggle] met en pause ou relance (WCAG 2.2.2).
 * - Héros : la vidéo ne se charge que sur grand écran, hors économie de
 *   données et mouvement réduit ; sinon l'image fixe reste.
 * - Animations au défilement (brief, section 4), toutes neutralisées en
 *   mouvement réduit :
 *   révélation [data-reveal] à 15 % de visibilité, une seule fois, 60 ms
 *   d'écart dans un [data-reveal-group] (4 éléments au plus) — un élément
 *   déjà à l'écran au chargement n'est jamais masqué ; ligne de relief
 *   [data-trace] et recadrage [data-clip] en repli quand animation-timeline
 *   n'existe pas ; compteur [data-count-to] sur 900 ms, une seule fois.
 * - Capture « prévenez-moi » : le pays saisi devient l'étiquette du contact.
 * - Comparaison relief / pendentif [data-compare] : la ligne suit le doigt ou
 *   la souris, et le clavier par le curseur natif masqué.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && !dialog.open) {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // Le fond d'un dialog modal renvoie le dialog lui-même comme cible.
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      event.target.close();
    }
  });

  // Lecture seulement à l'écran ; en mouvement réduit, la vidéo attend un geste.
  document.querySelectorAll('[data-autoplay-video]').forEach((wrapper) => {
    const video = wrapper.querySelector('video');
    const toggle = wrapper.querySelector('[data-video-toggle]');
    if (!video) return;
    let paused = reduceMotion.matches;
    let inView = false;

    const sync = () => {
      if (inView && !paused) video.play().catch(() => {});
      else video.pause();
      toggle?.setAttribute('aria-pressed', String(paused));
    };

    toggle?.addEventListener('click', () => {
      paused = !paused;
      sync();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          sync();
        },
        { threshold: 0.25 }
      ).observe(video);
    }
    sync();
  });

  /* ---------- Héros ---------- */

  // Ordinateur : la vidéo 1080p. Mobile : la version 720p si le héros la
  // propose, jamais en économie de données ni sur connexion lente.
  const heroVideo = document.querySelector('[data-hero-video]');
  const desktop = window.matchMedia('(min-width: 990px)').matches;
  const slow = /(^|-)(2g|3g)$/.test(navigator.connection?.effectiveType || '');
  const heroSource = desktop ? heroVideo?.dataset.src : heroVideo?.dataset.srcMobile;
  if (heroVideo && heroSource && !navigator.connection?.saveData && !slow && !reduceMotion.matches) {
    const toggle = document.querySelector('[data-hero-toggle]');
    const start = () => {
      heroVideo.src = heroSource;
      heroVideo.addEventListener('playing', () => heroVideo.classList.add('is-playing'), { once: true });
      heroVideo.play().catch(() => {});
    };
    // Sur mobile, la vidéo attend que la page (et son image principale) soit chargée
    if (desktop || document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const paused = toggle.getAttribute('aria-pressed') !== 'true';
        toggle.setAttribute('aria-pressed', String(paused));
        if (paused) heroVideo.pause();
        else heroVideo.play().catch(() => {});
      });
    }
  }

  /* ---------- Vidéos différées (chapitres) ---------- */

  // Muettes, en boucle : rien n'est téléchargé avant que le visiteur arrive à
  // la vidéo. En mouvement réduit, elle ne démarre pas seule : commandes visibles.
  document.querySelectorAll('[data-lazy-video]').forEach((video) => {
    const start = () => {
      video.src = video.dataset.src;
      if (reduceMotion.matches) video.controls = true;
      else video.play().catch(() => { video.controls = true; });
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      start();
    }, { rootMargin: '200px' }).observe(video);
  });

  /* ---------- Animations au défilement ---------- */

  if (!reduceMotion.matches && 'IntersectionObserver' in window) {
    const once = (callback, threshold = 0.15) =>
      new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            callback(entry.target);
          });
        },
        { threshold }
      );

    const belowFold = (el) => el.getBoundingClientRect().top > window.innerHeight;

    // Révélation : la classe .reveal n'est posée qu'ici, jamais dans le HTML
    const revealer = once((el) => el.classList.add('is-in'));
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (!belowFold(el)) return;
      const group = el.closest('[data-reveal-group]');
      if (group) {
        const index = [...group.querySelectorAll('[data-reveal]')].indexOf(el);
        el.style.setProperty('--reveal-delay', `calc(var(--d-stagger) * ${Math.min(index % 4, 3)})`);
      }
      el.classList.add('reveal');
      revealer.observe(el);
    });

    // Repli de la ligne qui se trace et du recadrage, sans animation-timeline
    if (!CSS.supports('animation-timeline: view()')) {
      const drawer = once((el) => el.classList.add('is-drawn'));
      document.querySelectorAll('[data-trace]').forEach((el) => {
        if (!belowFold(el)) return;
        el.classList.add('trace');
        drawer.observe(el);
      });
      const unclipper = once((el) => el.classList.add('is-unclipped'));
      document.querySelectorAll('[data-clip]').forEach((el) => {
        if (!belowFold(el)) return;
        el.classList.add('clip');
        unclipper.observe(el);
      });
    }

    // Compteur : seulement pour un nombre réel, affiché tel quel sans JavaScript
    const counter = once((el) => {
      const target = Number(el.dataset.countTo);
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / 900);
        el.textContent = Math.round(target * (1 - (1 - progress) ** 3));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, 0.5);
    document.querySelectorAll('[data-count-to]').forEach((el) => {
      if (!belowFold(el)) return;
      el.textContent = '0';
      counter.observe(el);
    });
  }

  /* ---------- Capture « prévenez-moi » ---------- */

  document.addEventListener('submit', (event) => {
    const country = event.target.querySelector?.('[data-notify-country]');
    const tags = event.target.querySelector?.('[data-notify-tags]');
    if (!country || !tags || !country.value.trim()) return;
    const slug = country.value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40);
    tags.value = `newsletter, pays-demande-${slug}`;
  });

  /* ---------- Comparaison relief / pendentif ---------- */

  // La ligne suit un glissement horizontal, sans sauter sous le doigt ; un
  // glissement vertical reste un défilement de la page (touch-action: pan-y).
  // À la souris, un clic place aussi la ligne. Sans JavaScript, elle reste au
  // milieu.
  const DRAG_THRESHOLD = 6;
  document.querySelectorAll('[data-compare]').forEach((compare) => {
    const input = compare.querySelector('[data-compare-input]');
    if (!input) return;

    const place = (value) => {
      const position = Math.min(100, Math.max(0, Math.round(value)));
      input.value = position;
      compare.style.setProperty('--compare-pos', `${position}%`);
      input.setAttribute(
        'aria-valuetext',
        input.dataset.valueText.replace('[relief]', position).replace('[pendant]', 100 - position)
      );
    };

    // Premier geste : l'animation d'entrée s'arrête là où elle en est, sans saut
    const settle = () => {
      if (compare.classList.contains('is-touched')) return;
      const current = parseFloat(getComputedStyle(compare).getPropertyValue('--compare-pos'));
      if (!Number.isNaN(current)) place(current);
      compare.classList.add('is-touched');
    };

    input.addEventListener('keydown', settle);
    input.addEventListener('input', () => place(input.value));
    compare.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button > 0) return;
      settle();
      const box = compare.getBoundingClientRect();
      const mouse = event.pointerType === 'mouse';
      if (mouse) place(((event.clientX - box.left) / box.width) * 100);
      compare.setPointerCapture(event.pointerId);

      const start = { x: event.clientX, y: event.clientY, value: Number(input.value) };
      let dragging = mouse;
      const move = (moveEvent) => {
        const dx = moveEvent.clientX - start.x;
        const dy = moveEvent.clientY - start.y;
        if (!dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
          dragging = true;
        }
        place(start.value + (dx / box.width) * 100);
      };
      const stop = () => {
        compare.removeEventListener('pointermove', move);
        compare.removeEventListener('pointerup', stop);
        compare.removeEventListener('pointercancel', stop);
      };
      compare.addEventListener('pointermove', move);
      compare.addEventListener('pointerup', stop);
      compare.addEventListener('pointercancel', stop);
    });
    place(input.value);

    // Repli de l'animation d'entrée quand le navigateur ne la pilote pas au
    // défilement : même glissement, joué une fois à l'entrée dans l'écran.
    if (CSS.supports('animation-timeline: view()') || reduceMotion.matches || !('IntersectionObserver' in window)) return;
    const from = parseFloat(getComputedStyle(compare).getPropertyValue('--compare-from')) || 92;
    place(from);
    compare.classList.add('is-tilted');
    const entrance = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      entrance.disconnect();
      compare.classList.remove('is-tilted');
      const start = performance.now();
      const tick = (now) => {
        if (compare.classList.contains('is-touched')) return;
        const progress = Math.min(1, (now - start) / 900);
        place(from + (50 - from) * (1 - (1 - progress) ** 3));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    entrance.observe(compare);
  });
})();
